import { Service, computed, effect, inject, signal, untracked, type Signal } from '@angular/core'
import { HttpContext, type HttpErrorResponse } from '@angular/common/http'
import { LOCAL_ERROR_FEEDBACK } from '@awork/_shared/services/api-client/local-error-feedback'
import { Subject, finalize } from 'rxjs'
import { TrackingService } from '@awork/_shared/services/tracking-service/tracking.service'
import { TrackingEvent } from '@awork/_shared/services/tracking-service/events'
import { ZonedDatePipe } from '@awork/_shared/pipes/zoned-date.pipe'
import { apiEndpoint } from '@awork/environments/environment'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { UserQuery } from '@awork/features/user/state/user.query'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import replace from '@awork/_shared/functions/replace'
import { ToastService } from '../toast-service/toast.service'
import { UndoLabelService } from './undo-label.service'
import type { PendingUndo, UndoCaptureMetadata, UndoEntry, UndoResult, UndoSource } from './types'

export const UNDO_WINDOW_MS = 30 * 60 * 1000
const STORAGE_KEY = 'aw-undo-pending'
const EDITOR_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], .ProseMirror'
const OVERLAY_SELECTOR = '[role="dialog"], [aria-modal="true"], .cdk-overlay-pane, aw-modal, aw-popup'

@Service()
export class UndoService {
  private readonly apiClient = inject(ApiClient)
  private readonly toastService = inject(ToastService)
  private readonly userQuery = inject(UserQuery)
  private readonly trackingService = inject(TrackingService)
  private readonly featureFlagQuery = inject(FeatureFlagQuery)
  private readonly signalService = inject(SignalService)
  private readonly browserService = inject(BrowserService)
  private readonly undoLabelService = inject(UndoLabelService)

  private readonly currentUser = this.userQuery.queryCurrentUser()
  private readonly isEnabled = this.featureFlagQuery.queryFlag(FeatureFlag.undoActions)
  private readonly entry = signal<UndoEntry | null>(null)
  private floatingActionsVisible: Signal<boolean> = signal(true)

  readonly pending = computed(() => this.visibleEntry())
  readonly isBusy = signal(false)
  readonly completed$ = new Subject<{ mode: 'undo' | 'redo'; taskIds: string[] }>()

  /** Restores this tab's entry and expires it at its original deadline. */
  constructor() {
    effect(() => this.restoreForCurrentUser())
    effect(onCleanup => this.scheduleExpiry(this.entry(), onCleanup))
  }

  /**
   * Uses the framework's visibility without injecting its component-scoped service.
   * @param {Signal<boolean>} visible - Whether the floating bar is visible.
   */
  registerFloatingActionsVisibility(visible: Signal<boolean>): void {
    this.floatingActionsVisible = visible
  }

  /**
   * Limits the entry to its owner, the feature flag, and the undo window.
   * @withSignal {Signal<UndoEntry | null>} entry - The stored entry.
   * @withSignal {Signal<boolean>} isEnabled - The workspace flag.
   * @withSignal {Signal<User | null>} currentUser - The signed-in user.
   * @returns {PendingUndo | null} The entry with its current task label.
   */
  private visibleEntry(): PendingUndo | null {
    const entry = this.entry()

    if (
      !this.isEnabled() ||
      !entry ||
      entry.userId !== this.currentUser()?.id ||
      Date.now() - entry.registeredAt >= UNDO_WINDOW_MS
    ) {
      return null
    }

    return { ...entry, label: this.undoLabelService.build(entry) }
  }

  /** Waits for a signed-in user before reading this tab's entry. */
  private restoreForCurrentUser(): void {
    const userId = this.currentUser()?.id

    if (userId) {
      untracked(() => this.restore(userId))
    }
  }

  /**
   * Clears an entry at its deadline and cancels the previous timer.
   * @param {UndoEntry | null} entry - The current entry.
   * @param {(cleanup: () => void) => void} onCleanup - Effect cleanup registration.
   */
  private scheduleExpiry(entry: UndoEntry | null, onCleanup: (cleanup: () => void) => void): void {
    if (!entry) {
      return
    }

    const timer = setTimeout(() => this.clear(), Math.max(0, UNDO_WINDOW_MS - (Date.now() - entry.registeredAt)))

    onCleanup(() => clearTimeout(timer))
  }

  /**
   * Replaces the pending entry with the server's latest record.
   * @param {UndoCaptureMetadata} metadata - The response metadata.
   */
  register(metadata: UndoCaptureMetadata): void {
    const userId = this.currentUser()?.id

    if (!this.isEnabled() || !userId) {
      return
    }

    const entry: UndoEntry = { ...metadata, registeredAt: Date.now(), userId }

    this.setEntry(entry)

    if (!this.floatingActionsVisible()) {
      const label = this.undoLabelService.build(entry)

      this.toastService.show(
        replace(entry.mode === 'redo' ? q.translations.UndoService.redoLabel : q.translations.UndoService.undoLabel, {
          label
        }),
        {
          type: 'action',
          actionText: entry.mode === 'redo' ? q.translations.UndoService.redoAction : q.translations.UndoService.undo,
          closeDelay: 6000,
          showRepeated: true,
          action: () => {
            if (this.pending()?.id !== entry.id) {
              this.showInfo(q.translations.UndoService.expired)

              return
            }

            this.execute('toast')
          }
        }
      )
    }
  }

  /** Clears this tab's pending action. */
  clear(): void {
    this.setEntry(null)
  }

  /**
   * Handles the platform shortcuts outside editors and open overlays.
   * @param {KeyboardEvent} event - The browser keyboard event.
   */
  handleKeyboardEvent(event: KeyboardEvent): void {
    const mode = this.keyboardDirection(event)
    const entry = this.pending()

    if (!mode || event.defaultPrevented || event.repeat || this.isBusy() || !entry) {
      return
    }

    if (entry.mode !== mode || Date.now() - entry.registeredAt > UNDO_WINDOW_MS) {
      return
    }

    const target = event.target instanceof Element ? event.target : null
    const active = document.activeElement

    if (this.isKeyboardBlocked(target) || this.isKeyboardBlocked(active)) {
      return
    }

    event.preventDefault()

    this.execute('keyboard')
  }

  /**
   * Keeps native editor undo and blocking dialogs while allowing the task detail surface.
   * @param {Element | null} target - The event target or focused element.
   * @returns {boolean} Whether the focused surface owns the shortcut.
   */
  private isKeyboardBlocked(target: Element | null): boolean {
    if (target?.closest(EDITOR_SELECTOR)) {
      return true
    }

    for (let element = target; element; element = element.parentElement) {
      if (!element.matches(OVERLAY_SELECTOR)) {
        continue
      }

      // ModalService moves the task modal from its component host to document.body.
      const taskModal = element.closest('aw-modal.task-modal')
      const isTaskDetailSurface = taskModal && (element === taskModal || element.parentElement === taskModal)

      if (!isTaskDetailSurface || element.matches('.cdk-overlay-pane, aw-popup')) {
        return true
      }
    }

    return false
  }

  /**
   * Matches logical keys so QWERTZ Y cannot be interpreted as Undo.
   * @param {KeyboardEvent} event - The browser keyboard event.
   * @returns {'undo' | 'redo' | null} The requested direction, if any.
   */
  private keyboardDirection(event: KeyboardEvent): 'undo' | 'redo' | null {
    const key = event.key.toLowerCase()
    const isModifier = event.metaKey || event.ctrlKey

    if (!isModifier || event.altKey) {
      return null
    }

    if (key === 'z') {
      return event.shiftKey ? 'redo' : 'undo'
    }

    if (this.browserService.getOS() !== 'Mac' && event.ctrlKey && !event.shiftKey && key === 'y') {
      return 'redo'
    }

    return null
  }

  /**
   * Sends one inverse request; its response headers register the next direction.
   * @param {UndoSource} source - The initiating control.
   */
  execute(source: UndoSource): void {
    const entry = this.pending()

    if (!entry || this.isBusy()) {
      return
    }

    if (Date.now() - entry.registeredAt >= UNDO_WINDOW_MS) {
      this.clear()
      this.showInfo(q.translations.UndoService.expired)

      return
    }

    this.signalService.removeTemporaryExcludedSignals(
      entry.taskIds.flatMap(taskId => [
        { entityName: 'task', entityId: taskId, propertyName: 'taskStatusId' },
        { entityName: 'task', entityId: taskId, propertyName: 'taskStatus' }
      ])
    )
    this.trackingService.trackEvent(entry.mode === 'undo' ? TrackingEvent.undoRequested : TrackingEvent.redoRequested, {
      fields: entry.fields,
      itemCount: entry.count,
      source
    })
    this.isBusy.set(true)

    this.apiClient
      .post<UndoResult>(
        `${apiEndpoint}/undo/${entry.id}`,
        {},
        { context: new HttpContext().set(LOCAL_ERROR_FEEDBACK, true) }
      )
      .pipe(finalize(() => this.isBusy.set(false)))
      .subscribe({
        next: result => this.showResult(result, entry),
        error: (error: HttpErrorResponse) => {
          if (this.pending()?.id === entry.id) {
            this.clear()
          }

          this.showInfo(error.status === 404 ? q.translations.UndoService.expired : q.translations.UndoService.failed)
        }
      })
  }

  /**
   * Reports the result without replacing an inverse record from the response.
   * @param {UndoResult} result - The server result.
   * @param {PendingUndo} entry - The entry used for this request.
   */
  private showResult(result: UndoResult, entry: PendingUndo): void {
    if (this.pending()?.id === entry.id) {
      this.clear()
    }

    if (result.state === 'undone') {
      this.completed$.next({ mode: entry.mode, taskIds: result.affectedTaskIds })

      const translation =
        entry.mode === 'undo' ? q.translations.UndoService.undoneLabel : q.translations.UndoService.redoneLabel

      this.toastService.show(replace(translation, { label: entry.label }), {
        type: 'confirmation',
        closeDelay: 4000,
        showRepeated: true
      })
    } else if (result.state === 'expired' || result.state === 'alreadyUndone') {
      this.showInfo(q.translations.UndoService.expired)
    } else {
      this.showInfo(this.blockedMessage(result))
    }
  }

  /**
   * Builds the reason shown after a blocked undo.
   * @param {UndoResult} result - The blocked result.
   * @returns {string} The translated reason.
   */
  private blockedMessage(result: UndoResult): string {
    const translations = q.translations.UndoService

    if (result.reason === 'changedSince') {
      const time = new ZonedDatePipe().transform(result.changedOn, 'HH:mm') ?? ''

      if (result.changedBySystem) {
        return replace(translations.blockedChangedBySystem, { time })
      }

      if (result.changedByUserId === this.currentUser()?.id) {
        return replace(translations.blockedChangedByYou, { time })
      }

      const name = result.changedByUserId ? this.userQuery.getUser(result.changedByUserId)?.fullName : undefined

      if (name && time) {
        return replace(translations.blockedChangedBy, { name, time })
      }

      return translations.blockedChanged
    }

    if (result.reason === 'permission') {
      return translations.blockedPermission
    }

    if (result.reason === 'missing') {
      return translations.blockedMissing
    }

    if (result.reason === 'unsupported') {
      return translations.blockedUnsupported
    }

    if (result.reason === 'superseded') {
      return translations.expired
    }

    return translations.failed
  }

  /**
   * Uses the neutral style for expired, blocked and failed operations.
   * @param {string} message - The translated message.
   */
  private showInfo(message: string): void {
    this.toastService.show(message, { type: 'info', showRepeated: true })
  }

  /**
   * Persists state changes without making storage access a requirement.
   * @param {UndoEntry | null} entry - The new entry.
   */
  private setEntry(entry: UndoEntry | null): void {
    this.entry.set(entry)

    try {
      if (entry) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
      } else {
        sessionStorage.removeItem(STORAGE_KEY)
      }
    } catch {
      // Undo remains available in memory when storage is disabled.
    }
  }

  /**
   * Restores an unexpired entry only for its owner.
   * @param {string} userId - The signed-in user.
   */
  private restore(userId: string): void {
    if (this.entry()?.userId === userId) {
      return
    }

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      const entry: UndoEntry | null = stored ? JSON.parse(stored) : null

      if (
        entry?.userId === userId &&
        Date.now() - entry.registeredAt < UNDO_WINDOW_MS &&
        typeof entry.id === 'string' &&
        Array.isArray(entry.taskIds) &&
        Array.isArray(entry.fields)
      ) {
        this.entry.set(entry)
      } else {
        this.clear()
      }
    } catch {
      // A storage error must not block startup or a later capture.
    }
  }
}
